import { fromJS } from 'immutable';
import _ from 'lodash';
import * as actionTypes from '../actions/umoGalleryActionTypes';
import * as sessionActionTypes from '../actions/sessionActionTypes';
import * as categoriesActionTypes from '../actions/categoriesActionTypes';
import { SearchParams } from '../utils/searchUtils';
import appConfig from '../appConfig';
import {
  setLastSelectedUMOIDReducer,
  umoSelectCaseReducer,
  umoSetSelectedUMOIDsReducer,
} from '../utils/umoUtils';

function getDefaultSortBy() {
  let value = 'NONE';
  let isDescending = false;
  if (
    appConfig.umoGallery.sortByOptions &&
    Array.isArray(appConfig.umoGallery.sortByOptions)
  ) {
    for (let i = 0; i < appConfig.umoGallery.sortByOptions.length; i++) {
      if (appConfig.umoGallery.sortByOptions[i].default) {
        value = appConfig.umoGallery.sortByOptions[i].value;
        isDescending = appConfig.umoGallery.sortByOptions[i].isDescending;
      }
    }
  }
  return { value, isDescending };
}

function buildMetatags() {
  // get video and audio data for list View
  let metatags =
    'VIDEO.HEIGHT,VIDEO.WIDTH,VIDEO.DURATION,VIDEO.FILESIZE,AUDIO.FILESIZE,AUDIO.DURATION';

  if (appConfig.umoRatings && appConfig.umoRatings.display) {
    metatags = appConfig.umoRatings.overrideMetatag
      ? `${metatags},${appConfig.umoRatings.overrideMetatag}`
      : `${metatags},INTERNAL.RATING`;
  }

  if (
    appConfig.customClassMetatag &&
    !metatags.includes(appConfig.customClassMetatag)
  ) {
    metatags = `${metatags},${appConfig.customClassMetatag}`;
  }
  if (
    appConfig.showTitleOverGalleryUmo &&
    appConfig.umoTitleMetatag &&
    !metatags.includes(appConfig.umoTitleMetatag)
  ) {
    // bring the title metatag in tombstone data only if required to display on hover in umo gallery
    metatags = `${metatags},${appConfig.umoTitleMetatag}`;
  }
  if (
    appConfig.menuOptions.customWorkspaceMenu &&
    _.isArray(appConfig.menuOptions.customWorkspaceMenu)
  ) {
    appConfig.menuOptions.customWorkspaceMenu.forEach((item) => {
      if (item.metatag && !metatags.includes(item.metatag))
        metatags = `${metatags},${item.metatag}`;
    });
  }

  if (appConfig.listViewMetatags) {
    if (_.isArray(appConfig.listViewMetatags)) {
      appConfig.listViewMetatags.forEach((item) => {
        if (!metatags.includes(item)) metatags = `${metatags},${item}`;
      });
    } else metatags = `${metatags},${appConfig.listViewMetatags}`;
  }

  if (
    appConfig.sortItemMetatag &&
    !metatags.includes(appConfig.sortItemMetatag)
  ) {
    metatags = `${metatags},${appConfig.sortItemMetatag}`;
  }

  if (appConfig.customTooltipMetatag) {
    if (_.isArray(appConfig.customTooltipMetatag)) {
      appConfig.customTooltipMetatag.forEach((item) => {
        if (!metatags.includes(item)) metatags = `${metatags},${item}`;
      });
    } else if (!metatags.includes(appConfig.customTooltipMetatag)) {
      metatags = `${metatags},${appConfig.customTooltipMetatag}`;
    }
  }

  return metatags;
}
const initialSortBy = getDefaultSortBy();
const initialState = fromJS({
  request: null,
  isRequesting: false,
  isRefreshRequest: false,
  searchParams: new SearchParams(),
  prevSearches: [],
  sortBy: initialSortBy.value,
  sortByIsDescending: !!initialSortBy.isDescending,
  metatags: buildMetatags(),
  umoList: [],
  umoTotal: null,
  umosPerPage: appConfig.umoGallery.umosPerPage,
  pageNumber: 1,
  pageTotal: null,
  selectedUMOIDs: [],
  isSelectAll: false,
  savedSearches: [],
  galleryType: SearchParams.searchTypes.GENERAL,
  lastSelectedUMOID: null,
  isFilterExpanded: false,
  selectedCategory: null,
  imageEditModal: {
    isVisible: false,
    modalType: null,
    umoUrl: null,
    umoId: null,
  },
});

const cancelPromise = (promise) =>
  promise && promise.cancel && promise.cancel();

const umoGallery = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.UMOGALLERY_SEARCH_RETRIEVE: {
      cancelPromise(state.get('request'));
      const hasSearchParamsChanged = action.searchParams !== state.get('searchParams');
      let savedSearches = state.get('prevSearches').slice();
      if (action.prevSearch.get('displayTags')) {
        savedSearches = savedSearches.splice(
          savedSearches.size,
          0,
          action.prevSearch,
        );
      }
      return state
        .set('prevSearches', savedSearches)
        .set('searchParams', action.searchParams)
        .set('request', action.requestPromise)
        .set('isRequesting', true)
        .set('umosPerPage', action.umosPerPage || state.get('umosPerPage'))
        .set('pageNumber', action.pageNumber || state.get('pageNumber'))
        .set('sortBy', action.sortBy || state.get('sortBy'))
        .set(
          'sortByIsDescending',
          action.sortByIsDescending || state.get('sortByIsDescending')
        )
        .set(
          'selectedUMOIDs',
          hasSearchParamsChanged ? fromJS([]) : state.get('selectedUMOIDs')
        )
        .set(
          'isSelectAll',
          hasSearchParamsChanged ? false : state.get('isSelectAll')
        );
    }
    case actionTypes.UMOGALLERY_SEARCH_SUCCESS: {
      const umoTotal = parseInt(action.queryData.t, 10);
      return state
        .set('request', null)
        .set('isRequesting', false)
        .set('pageNumber', state.get('pageNumber'))
        .set('pageTotal', Math.ceil(umoTotal / state.get('umosPerPage')))
        .set('isRefreshRequest', false)
        .set('umoTotal', umoTotal)
        .set('umoList', fromJS(action.umoList));
    }
    case actionTypes.UMOGALLERY_SEARCH_FAILURE:
      return state
        .set('request', action.err)
        .set('isRequesting', false)
        .set('pageNumber', 1)
        .set('umoTotal', null)
        .set('isRefreshRequest', false)
        .set('umoList', fromJS([]));
    case actionTypes.UMOGALLERY_UPDATE_UMOSPERPAGE_RETRIEVE:
      cancelPromise(state.get('request'));
      return state
        .set('request', action.requestPromise)
        .set('isRequesting', true)
        .set('isRefreshRequest', true)
        .set('pageNumber', 1)
        .set('umosPerPage', action.umosPerPage);
    case actionTypes.UMOGALLERY_UPDATE_SORTBY_RETRIEVE:
      cancelPromise(state.get('request'));
      return state
        .set('request', action.requestPromise)
        .set('isRequesting', true)
        .set('isRefreshRequest', true)
        .set('sortBy', action.sortBy)
        .set('sortByIsDescending', action.sortByIsDescending);
    case actionTypes.UMOGALLERY_UPDATE_PAGE_RETRIEVE:
      cancelPromise(state.get('request'));
      return state
        .set('request', action.requestPromise)
        .set('isRequesting', true)
        .set('isRefreshRequest', true)
        .set('pageNumber', action.pageNumber);
    case actionTypes.UMOGALLERY_SELECT_TOGGLE: {
      const newState = umoSelectCaseReducer(state, action);

      // if "select all" option is on and the user has deselected the same amount of umos
      // that the query could return, assume that all has been deselected and reset
      // selection
      if (
        newState.get('isSelectAll') &&
        newState.get('umoTotal') === newState.get('selectedUMOIDs').size
      ) {
        return newState
          .set('isSelectAll', false)
          .set('selectedUMOIDs', fromJS([]));
      }
      return umoSelectCaseReducer(state, action);
    }
    case actionTypes.UMOGALLERY_SET_SELECTED_UMOIDS: {
      return umoSetSelectedUMOIDsReducer(state, action);
    }
    case actionTypes.UMOGALLERY_SELECT_PAGE:
      return state
        .set(
          'selectedUMOIDs',
          state.get('umoList').map((umo) => umo.toJS().id[0])
        )
        .set('isSelectAll', false);
    case actionTypes.UMOGALLERY_SELECT_SEARCH:
      return state.set('selectedUMOIDs', fromJS([])).set('isSelectAll', true);
    case actionTypes.UMOGALLERY_DESELECT_ALL:
      return state.set('selectedUMOIDs', fromJS([])).set('isSelectAll', false);
    case actionTypes.UMOGALLERY_CLEAR_SEARCH:
      return state.set('searchParams', new SearchParams());
    case sessionActionTypes.UPDATE_STORE_FROM_APPCONFIG: {
      const sortBy = getDefaultSortBy();
      return state
        .set('sortBy', sortBy.value)
        .set('sortByIsDescending', sortBy.isDescending)
        .set('metatags', buildMetatags())
        .set('umosPerPage', appConfig.umoGallery.umosPerPage);
    }
    case actionTypes.ADVANCED_SEARCH_SAVE:
      return state.set('savedSearches', action.searchArray);
    case actionTypes.UMOGALLERY_UPDATE_PREV_SEARCH:
      return state.set('prevSearches', state.get('prevSearches').pop().pop());
    case actionTypes.UMOGALLERY_SET_GALLERY_TYPE: {
      return state.set('galleryType', action.gType);
    }
    case actionTypes.UMOGALLERY_SET_LAST_SELECTED_UMO_ID: {
      return setLastSelectedUMOIDReducer(state, action);
    }
    case actionTypes.UMOGALLERY_TOGGLE_FILTER_MENU: {
      return state.set('isFilterExpanded', action.value);
    }
    case actionTypes.UMOGALLERY_SET_SELECTED_CATEGORY: {
      return state.set('selectedCategory', action.category);
    }
    case categoriesActionTypes.CATEGORIES_PURGE_RECYCLEBIN_SUCCESS:
      return state
        .set('umoList', fromJS([]))
        .set('umoTotal', 0)
        .set('isRequesting', false);
    default:
      return state;
  }
};

export default umoGallery;
